import * as ko from "knockout";
import * as Constants from "../common/constants";
import * as UI from "../common/ui";
import * as templateAccessRoleData from "../data/templateAccessRoleData";
import { ITemplateAccessRoleTemplate, ITemplateAccessRoleTemplateObservable,
    ITemplateAccessRoleUser, ITemplateAccessRoleUserObservable } from "../data/templateAccessRoleData";
import { IController } from "../interfaces/controller";
import * as Data from "../interfaces/data";

interface IViewModel {
    ID: KnockoutObservable<number>;
    Name: KnockoutObservable<string>;
    IsActive: KnockoutObservable<boolean>;
    UsersInRole: KnockoutObservableArray<ITemplateAccessRoleUserObservable>;
    TemplatesInRole: KnockoutObservableArray<ITemplateAccessRoleTemplateObservable>;
    errors?: KnockoutValidationErrors;
    selectAllUsers: () => void;
    deselectAllUsers: () => void;
    selectAllTemplates: () => void;
    deselectAllTemplates: () => void;
}

export class TemplateAccessRoleController implements IController {
    private fieldsOnLoad: string = "";
    private container: JQuery;
    private viewModel: IViewModel;
    private roleID: number;

    constructor() {
        this.viewModel = {
            ID: ko.observable(0),
            Name: ko.observable("").extend({ required: true }),
            IsActive: ko.observable(true),
            UsersInRole: ko.observableArray(),
            TemplatesInRole: ko.observableArray(),
            selectAllUsers: () => { this.toggleIsActive(true, this.viewModel.UsersInRole); },
            deselectAllUsers: () => { this.toggleIsActive(false, this.viewModel.UsersInRole); },
            selectAllTemplates: () => { this.toggleIsActive(true, this.viewModel.TemplatesInRole); },
            deselectAllTemplates: () => { this.toggleIsActive(false, this.viewModel.TemplatesInRole); },
        };
        this.viewModel.errors = ko.validation.group(this.viewModel, { deep: true });
    }

    public render(container: JQuery<HTMLElement>): JQueryPromise<any> {
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();

        this.container = container;

        ko.applyBindings(this.viewModel, this.container[0]);

        deferred.resolve();
        return deferred.promise();
    }

    public load(params: any[]): JQueryPromise<any> {
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        const me = this;

        if (params != null && params.length === 1) {
            this.roleID = params[0];
        } else {
            this.roleID = 0;
        }

        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(me.container);

        templateAccessRoleData.GetByID(this.roleID)
            .then((response) => {
                this.bindModel(response);
                deferred.resolve();
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
            });

        return deferred.promise();
    }

    public haveFieldsChanged(): boolean {
        const fieldValues: string = ko.toJSON(this.viewModel);
        return fieldValues !== this.fieldsOnLoad;
    }

    public save(): JQueryPromise<any> {
        const me = this;
        let waitContainer: JQuery;
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();

        if (this.isValid()) {
            waitContainer = UI.showWaitIndicator(me.container);
            templateAccessRoleData.Save(this.getTemplateAccessRole())
                .done((response: Data.IResponse<templateAccessRoleData.ITemplateAccessRole>) => {
                    if (response.Success) {
                        UI.showNotificationMessage(Constants.PHRASE_RECORDSAVED, UI.messageTypeValues.success);
                        me.bindModel(response);
                        deferred.resolve();
                    } else {
                        UI.showErrorMessage(response.ErrorMessages);
                        deferred.reject();
                    }
                })
                .fail(() => {
                    deferred.reject();
                })
                .always(() => {
                    UI.hideWaitIndicator(me.container, waitContainer);
                });
        } else {
            // not all valid fields are entered
            deferred.reject();
        }

        return deferred.promise();
    }

    public appRouterID(params: any[]): string {
        let appRouterID: string = "TemplateAccessRole";
        if (params != null && params.length === 1) {
            appRouterID += params[0];
        }
        return appRouterID;
    }

    public title(): string {
        return "Template Access Role " + (this.viewModel.Name() ? this.viewModel.Name() : "New");
    }

    public subTitle(): string {
        return "";
    }

    private bindModel(response: Data.IResponse<templateAccessRoleData.ITemplateAccessRole>) {
        this.viewModel.Name(response.Payload.Name);
        this.viewModel.ID(response.Payload.ID);
        this.viewModel.IsActive(response.Payload.IsActive);
        this.getTemplateObservableList(response.Payload.TemplatesInRole, this.viewModel.TemplatesInRole);
        this.getUserObservableList(response.Payload.UsersInRole, this.viewModel.UsersInRole);
        this.fieldsOnLoad = ko.toJSON(this.viewModel);
    }

    private isValid(): boolean {

        if (this.viewModel.errors && this.viewModel.errors().length > 0) {
            this.viewModel.errors.showAllMessages();
            UI.showErrorMessage([Constants.PHRASE_INVALIDFIELDSONSAVE]);
            return false;
        }

        return true;
    }

    private getTemplateObservableList(templates: ITemplateAccessRoleTemplate[], templateObservable: KnockoutObservableArray<ITemplateAccessRoleTemplateObservable>) {
        templateObservable.removeAll();

        for (const t of templates) {
            templateObservable.push({
                ID: ko.observable(t.ID),
                TemplateAccessRoleID: ko.observable(t.TemplateAccessRoleID),
                TemplateID: ko.observable(t.TemplateID),
                TemplateName: ko.observable(t.TemplateName),
                IsActive: ko.observable(t.IsActive),
                TemplateAccessRoleName: ko.observable(t.TemplateAccessRoleName),
            });
        }
    }

    private getUserObservableList(users: ITemplateAccessRoleUser[], userObservable: KnockoutObservableArray<ITemplateAccessRoleUserObservable>) {
        userObservable.removeAll();
        for (const u of users) {
            userObservable.push({
                ID: ko.observable(u.ID),
                TemplateAccessRoleID: ko.observable(u.TemplateAccessRoleID),
                UserID: ko.observable(u.UserID),
                UserName: ko.observable(u.UserName),
                IsActive: ko.observable(u.IsActive),
            });
        }
    }

    private getTemplateAccessRole(): templateAccessRoleData.ITemplateAccessRole {
        return {
            ID: this.viewModel.ID(),
            Name: this.viewModel.Name(),
            IsActive: this.viewModel.IsActive(),
            TemplatesInRole: this.getTemplateAccessRoleTemplatesFromObservable(this.viewModel.TemplatesInRole),
            UsersInRole: this.getTemplateAccessRoleUsersFromObservable(this.viewModel.UsersInRole),
        };
    }

    private getTemplateAccessRoleTemplatesFromObservable(templates: KnockoutObservableArray<ITemplateAccessRoleTemplateObservable>): ITemplateAccessRoleTemplate[] {
        const result: ITemplateAccessRoleTemplate[] = [];
        for (const t of templates()) {
            result.push({
                ID: t.ID(),
                IsActive: t.IsActive(),
                TemplateName: t.TemplateName(),
                TemplateID: t.TemplateID(),
                TemplateAccessRoleID: t.TemplateAccessRoleID(),
                TemplateAccessRoleName: "",
            });
        }
        return result;
    }

    private getTemplateAccessRoleUsersFromObservable(users: KnockoutObservableArray<ITemplateAccessRoleUserObservable>): ITemplateAccessRoleUser[] {
        const result: ITemplateAccessRoleUser[] = [];
        for (const u of users()) {
            result.push({
                ID: u.ID(),
                IsActive: u.IsActive(),
                TemplateAccessRoleID: u.TemplateAccessRoleID(),
                UserID: u.UserID(),
                UserName: u.UserName(),
            });
        }
        return result;
    }

    private toggleIsActive(isActive: boolean,
        items: KnockoutObservableArray<ITemplateAccessRoleTemplateObservable> | KnockoutObservableArray<ITemplateAccessRoleUserObservable>) {
        for (const item of items()) {
            item.IsActive(isActive);
        }
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--7!./src/script/controllers/templateAccessRole.ts